/* eslint-disable @stylistic/js/max-len */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import style from './kinde-phone-input.module.less';
import { useTranslation } from 'react-i18next';
import { Modal, ModalRef, PhoneInput } from '@law-connect/react-components';
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import selectors from '../../redux/selectors';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { toast } from 'react-toastify';
import {
  COLLECT_PHONE_NUMBER_COOKIE,
  PREMATTER_ID_COOKIE,
} from '../../constants';
import { actions } from '../../redux/slices';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
export const KindePhoneInput: React.FC = () => {
  const { t } = useTranslation();
  const [phoneNumber, setPhoneNumber] = useState('');
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);

  const phoneNumberModalRef = useRef<ModalRef>(null);

  const dispatch = useAppDispatch();

  const user = useAppSelector(selectors.user.getUser());

  const onContinue = useCallback(async () => {
    setIsLoading(true);
    const prematterId = window.sessionStorage.getItem(PREMATTER_ID_COOKIE);

    if (!user?.phone && !isValidPhoneNumber(phoneNumber)) {
      toast.error('Invalid phone number');
      setIsLoading(false);
      return;
    }

    if (prematterId) {
      try {
        await dispatch(
          actions.prematter.requestQuote({
            id: prematterId,
            phoneNumber: user?.phone ? undefined : phoneNumber,
          })
        ).unwrap();
        toast.success('Quote requested');
      } catch (error) {
        console.error('error', error);
        toast.error('Unable to request quote. You can try again later.');
        Sentry.captureException(error);
      }
      setIsLoading(false);

      window.sessionStorage.removeItem(COLLECT_PHONE_NUMBER_COOKIE);
      window.sessionStorage.removeItem(PREMATTER_ID_COOKIE);

      navigate(`/case/${prematterId}`);
    } else {
      try {
        await dispatch(actions.user.update({ phone: phoneNumber })).unwrap();
        dispatch(actions.user.fetch());
      } catch (error) {
        toast.error('Unable to update phone number. You can try again later.');
        Sentry.captureException(error);
        return;
      }

      window.sessionStorage.removeItem(COLLECT_PHONE_NUMBER_COOKIE);
      window.sessionStorage.removeItem(PREMATTER_ID_COOKIE);
      setIsLoading(false);

      navigate('/account');
    }

  }, [user?.phone, phoneNumber, navigate, dispatch]);

  useEffect(() => {
    if (user?.phone) {
      setPhoneNumber(user.phone);
      window.sessionStorage.removeItem(COLLECT_PHONE_NUMBER_COOKIE);
      const prematterId = window.sessionStorage.getItem(PREMATTER_ID_COOKIE);
      if (prematterId) {
        window.sessionStorage.removeItem(PREMATTER_ID_COOKIE);
        window.location.href = `/case/${prematterId}`;
      } else {
        window.sessionStorage.removeItem(PREMATTER_ID_COOKIE);
        window.location.href = '/account';
      }
    }
  }, [user.phone, setPhoneNumber]);

  useEffect(() => {
    phoneNumberModalRef.current?.openModal();
  }, [phoneNumberModalRef]);

  return (
    <Modal
      ref={phoneNumberModalRef}
      title={'Phone number'}
      description={'Enter your phone number for a lawyer to contact.'}
      primaryButton={{
        label: 'Continue',
        onClick: onContinue,
        pending: isLoading,
        disabled: isLoading,
      }}
      useAuthStyle={true}
      hideCloseButton={true}
      fullscreen={true}
    >
      <div className={style.phoneInputContainer}>
        <div className={style.phoneInputLabel}>Phone</div>
        <PhoneInput
          value={phoneNumber}
          onChange={setPhoneNumber}
          className={style.phoneInput}
          disabled={isLoading}
          props={{
            style: {
              backgroundColor: 'var(--white)',
            },
          }}
        />
      </div>
    </Modal>
  );
};
